import { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import server from '../../../global/server';
import { CONTATOS } from '../../../global/contatos';
import { getServerNow, useServerTime } from '../../../hooks/useServerTime';

const HORAS_MINIMAS = 24;
// Art. 49 do CDC, item Q dos Termos e item L da Politica de Compra e Pagamento
const DIAS_DE_ARREPENDIMENTO = 7;

const emReais = valor =>
    Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatar = data =>
    data ? new Date(data).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/**
 * Cancelamento da vaga pelo proprio conviva.
 *
 * As regras moram no servidor (BookingController.cancelBooking). Aqui a tela
 * antecipa o desfecho, porque ninguem deve confirmar um cancelamento sem saber
 * antes se o dinheiro volta:
 *
 *   gratuito, faltando 24h ou mais  -> cancela, nao ha valor envolvido
 *   gratuito, faltando menos de 24h -> nao pode cancelar, e o botao nem aparece
 *   pago, faltando 24h ou mais      -> cancela com reembolso integral
 *   pago, comprado ha ate 7 dias    -> cancela com reembolso integral, mesmo em
 *                                      cima da hora (direito de arrependimento)
 *   pago, faltando menos de 24h     -> cancela SEM reembolso, com aceite explicito
 *
 * Quando o anfitriao trocou o horario deste pedido, quem cuida do cancelamento
 * e o HorarioAlterado: la o reembolso vale mesmo em cima da hora, porque a
 * mudanca nao foi escolha do conviva.
 */
const CancelarVaga = ({ orderId, datetime, criadoEm, gratuito, valor, status, onCancelado }) => {
    const { enqueueSnackbar } = useSnackbar();
    useServerTime();
    const [aberto, setAberto] = useState(false);
    const [aceitouSemReembolso, setAceitou] = useState(false);
    const [cancelando, setCancelando] = useState(false);

    useEffect(() => {
        if (aberto) setAceitou(false);
    }, [aberto]);

    const horasAte = (new Date(datetime).getTime() - getServerNow().getTime()) / (1000 * 60 * 60);
    const dentroDoPrazo = horasAte >= HORAS_MINIMAS;

    // Pagamento ainda pendente nao gerou cobranca concluida: nao ha valor a
    // devolver, entao tambem nao ha nada para o conviva aceitar perder
    const pagamentoConcluido = !gratuito && status === 'CONCLUDED';

    // Direito de arrependimento: dentro de 7 dias corridos da compra o valor
    // volta integral, sem justificativa, desde que o encontro nao tenha
    // acontecido. Passa por cima do prazo de 24h. So vale para compra --
    // encontro gratuito nao tem valor pago a devolver.
    const diasDesdeAContratacao = criadoEm
        ? (getServerNow().getTime() - new Date(criadoEm).getTime()) / (1000 * 60 * 60 * 24)
        : Infinity;
    const dentroDoArrependimento =
        !gratuito && diasDesdeAContratacao <= DIAS_DE_ARREPENDIMENTO && horasAte > 0;

    const bloqueadoPeloPrazo = gratuito && !dentroDoPrazo;
    const comReembolso = pagamentoConcluido && (dentroDoPrazo || dentroDoArrependimento);
    const perdeOValor = pagamentoConcluido && !dentroDoPrazo && !dentroDoArrependimento;
    // Só quem pagou tem valor a perder. Pedido com cobranca em aberto e anulado
    // sem aceite nenhum: nao ha dinheiro envolvido.
    const enviarAceite = perdeOValor;

    const cancelar = async () => {
        try {
            setCancelando(true);
            const { data } = await server.delete('/booking/cancel', {
                data: {
                    order_id: orderId,
                    ...(enviarAceite ? { accept_no_refund: true } : {}),
                },
            });
            enqueueSnackbar(data?.message || 'Vaga cancelada', { variant: 'success' });
            setAberto(false);
            onCancelado?.();
        } catch (error) {
            enqueueSnackbar(error.response?.data?.message || 'Erro ao cancelar a vaga', {
                variant: 'error',
            });
        } finally {
            setCancelando(false);
        }
    };

    // Sem saida possivel: melhor explicar o porque do que deixar a tela muda
    if (bloqueadoPeloPrazo) {
        return (
            <Alert severity="info" sx={{ width: '100%', borderRadius: '12px' }}>
                Encontros gratuitos não podem ser cancelados com menos de {HORAS_MINIMAS} horas de
                antecedência. Se não puder comparecer, avise o anfitrião.
            </Alert>
        );
    }

    return (
        <>
            <Button
                variant="text"
                fullWidth
                onClick={() => setAberto(true)}
                sx={{ textTransform: 'none', color: '#c62828', fontWeight: 500 }}
            >
                Cancelar minha vaga
            </Button>

            <Dialog
                open={aberto}
                onClose={() => setAberto(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Cancelar sua vaga?</DialogTitle>

                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Encontro em <strong>{formatar(datetime)}</strong>
                        </Typography>

                        {comReembolso && (
                            <Alert severity="success" sx={{ borderRadius: '12px' }}>
                                Você receberá <strong>{emReais(valor)}</strong> de volta, integralmente.
                                {dentroDoArrependimento && !dentroDoPrazo && (
                                    <>
                                        {' '}
                                        A compra foi feita há menos de {DIAS_DE_ARREPENDIMENTO} dias, então
                                        o prazo de {HORAS_MINIMAS} horas não se aplica: é o seu direito de
                                        arrependimento.
                                    </>
                                )}
                            </Alert>
                        )}

                        {perdeOValor && (
                            <Alert severity="error" sx={{ borderRadius: '12px' }}>
                                Faltam menos de {HORAS_MINIMAS} horas para o encontro, então{' '}
                                <strong>não haverá reembolso</strong>: os {emReais(valor)} que você pagou
                                não serão devolvidos.
                            </Alert>
                        )}

                        {gratuito && (
                            <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                                Sua vaga será liberada para outra pessoa. Não há valor envolvido neste
                                encontro.
                            </Alert>
                        )}

                        {!gratuito && !pagamentoConcluido && (
                            <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                Seu pagamento ainda não foi concluído, então não há valor a devolver.
                            </Alert>
                        )}

                        <Typography variant="body2" color="text.secondary">
                            O anfitrião será avisado. Esta ação é irreversível.
                        </Typography>

                        {/* Item 3.4.1 da Politica de Compra e Pagamento: este
                            endereco e o chat da Plataforma sao os unicos canais
                            validos para pedir reembolso. Quem cancela em cima da
                            hora e o unico que sai daqui sem o dinheiro, entao e
                            a ele que o caminho interessa. */}
                        {perdeOValor && (
                            <Typography variant="caption" color="text.secondary">
                                Se você acha que o seu caso comporta reembolso, escreva para{' '}
                                <strong>{CONTATOS.reembolso}</strong> antes de cancelar.
                            </Typography>
                        )}

                        {perdeOValor && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={aceitouSemReembolso}
                                        onChange={e => setAceitou(e.target.checked)}
                                        sx={{ '&.Mui-checked': { color: '#c62828' } }}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        Entendo que não receberei o valor de volta
                                    </Typography>
                                }
                            />
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAberto(false)} sx={{ textTransform: 'none', color: '#666' }}>
                        Voltar
                    </Button>
                    <LoadingButton
                        onClick={cancelar}
                        loading={cancelando}
                        disabled={perdeOValor && !aceitouSemReembolso}
                        variant="contained"
                        color="error"
                        sx={{ textTransform: 'none' }}
                    >
                        {comReembolso ? 'Cancelar e ser reembolsado' : 'Cancelar minha vaga'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CancelarVaga;
